import {
  PublicClientApplication,
  EventType,
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';

// Spec §15.4 / §17.3. Per-tenant config is fetched from the backend at startup so
// nothing tenant-specific is baked into the SPA bundle.
export interface MsalRuntimeConfig {
  tenantId: string;
  clientId: string;
  apiScope: string;
}

let pca: PublicClientApplication | undefined;
let runtime: MsalRuntimeConfig | undefined;

// sessionStorage key so a Vite HMR reload of this module can still recover
// the runtime config (and therefore ensureMsal can re-init the PCA).
const RUNTIME_CACHE_KEY = '__af_msal_runtime';
try {
  const cached = sessionStorage.getItem(RUNTIME_CACHE_KEY);
  if (cached) runtime = JSON.parse(cached) as MsalRuntimeConfig;
} catch { /* ignore – fresh load will repopulate */ }

export async function initMsal(cfg: MsalRuntimeConfig): Promise<PublicClientApplication> {
  if (pca) return pca;
  runtime = cfg;
  try {
    sessionStorage.setItem(RUNTIME_CACHE_KEY, JSON.stringify(cfg));
  } catch { /* sessionStorage full or unavailable – non-fatal */ }

  const msalConfig: Configuration = {
    auth: {
      clientId: cfg.clientId,
      authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  };

  pca = new PublicClientApplication(msalConfig);
  await pca.initialize();

  const result = await pca.handleRedirectPromise();
  if (result?.account) {
    pca.setActiveAccount(result.account);
  } else {
    const accounts = pca.getAllAccounts();
    if (accounts.length > 0 && accounts[0]) {
      pca.setActiveAccount(accounts[0]);
    }
  }

  pca.addEventCallback((evt) => {
    if (
      (evt.eventType === EventType.LOGIN_SUCCESS ||
        evt.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
      evt.payload &&
      'account' in evt.payload
    ) {
      const account = (evt.payload as AuthenticationResult).account;
      if (account) pca?.setActiveAccount(account);
    }
  });

  return pca;
}

export function getMsal(): PublicClientApplication {
  if (!pca) throw new Error('MSAL not initialised – call initMsal first');
  return pca;
}

// Auto-recovering accessor. If the module-level pca was lost (Vite HMR
// reloaded msal.ts, sessionStorage was cleared, etc.) but we still know the
// runtime config from a successful prior init, re-init transparently.
// Used by login / logout / acquireToken so a transient MSAL state loss
// doesn't surface as an uncaught-promise error in the console.
async function ensureMsal(): Promise<PublicClientApplication> {
  if (pca) return pca;
  if (!runtime) throw new Error('MSAL not initialised and no runtime config cached');
  return initMsal(runtime);
}

export function getActiveAccount(): AccountInfo | null {
  return pca?.getActiveAccount() ?? null;
}

export async function login(): Promise<void> {
  const c = await ensureMsal();
  const scopes = runtime ? [runtime.apiScope] : [];
  await c.loginRedirect({ scopes });
}

export async function logout(): Promise<void> {
  const c = await ensureMsal();
  await c.logoutRedirect();
}

// Returns a fresh access token for the configured API scope, falling back to an
// interactive redirect if silent acquisition fails.
export async function acquireToken(): Promise<string> {
  const c = await ensureMsal();
  const account = c.getActiveAccount();
  if (!account) throw new InteractionRequiredAuthError('no active account');
  if (!runtime) throw new Error('MSAL runtime config missing');

  try {
    const result = await c.acquireTokenSilent({ scopes: [runtime.apiScope], account });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await c.acquireTokenRedirect({ scopes: [runtime.apiScope] });
      throw err;
    }
    throw err;
  }
}
